import { useEffect, useState } from 'react';

export function isDirectMediaUrl(url: string): boolean {
  return (
    url.startsWith('blob:') ||
    url.startsWith('data:') ||
    /^https?:\/\//i.test(url)
  );
}

export function resolveMediaFetchUrl(apiPath: string): string {
  if (/^https?:\/\//i.test(apiPath)) {
    return apiPath;
  }
  return apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
}

/** Fetch a gated /api/profile/.../media URL with Bearer auth and return a blob object URL. */
export async function fetchAuthenticatedMediaBlobUrl(
  apiPath: string | undefined | null,
): Promise<string | undefined> {
  if (!apiPath?.trim()) {
    return undefined;
  }
  if (isDirectMediaUrl(apiPath)) {
    return apiPath;
  }

  const token = localStorage.getItem('accessToken');
  const response = await fetch(resolveMediaFetchUrl(apiPath), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!response.ok) {
    return undefined;
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/** Resolve gated profile media paths for use in img/Avatar (browser cannot send Bearer on src). */
export function useAuthenticatedMediaUrl(apiPath: string | undefined | null): string | undefined {
  const [src, setSrc] = useState<string | undefined>();

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;

    if (!apiPath?.trim()) {
      setSrc(undefined);
      return undefined;
    }

    if (isDirectMediaUrl(apiPath)) {
      setSrc(apiPath);
      return undefined;
    }

    void fetchAuthenticatedMediaBlobUrl(apiPath).then((url) => {
      if (cancelled) {
        if (url && !isDirectMediaUrl(url)) {
          URL.revokeObjectURL(url);
        }
        return;
      }
      objectUrl = url;
      setSrc(url);
    });

    return () => {
      cancelled = true;
      if (objectUrl && !isDirectMediaUrl(objectUrl)) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [apiPath]);

  return src;
}
